import { z } from 'zod';
import { IsoDateTimeSchema, TenantIdSchema, UuidSchema } from './common';

/**
 * Webhook esemény-típusok — az ARCHITECTURE.md 2.4-ben tervezett értékek.
 * Új típus felvételekor a backend-en is regisztrálni kell, az UI dropdown
 * automatikusan megjeleníti.
 */
export const WebhookEventSchema = z.enum([
  'book.published',
  'book.unpublished',
  'book.retired',
  'reader.finished',
  'reader.progress',
  'conversion.failed',
  'conversion.completed',
  'license.issued',
  'license.revoked',
]);
export type WebhookEvent = z.infer<typeof WebhookEventSchema>;

export const WebhookSchema = z.object({
  id: UuidSchema,
  tenantId: TenantIdSchema,
  url: z.string().url(),
  events: z.array(WebhookEventSchema).min(1),
  active: z.boolean(),
  /** A backend itt csak metadata-t ad (utolsó 4 karakter), a teljes secret-et soha. */
  secretMasked: z.string(),
  description: z.string().max(500).nullable().optional(),
  createdAt: IsoDateTimeSchema,
  /** Utolsó kézbesítés állapota — gyors monitorozáshoz */
  lastDeliveryStatus: z.enum(['pending', 'success', 'failed']).nullable(),
  lastDeliveryAt: IsoDateTimeSchema.nullable(),
});
export type Webhook = z.infer<typeof WebhookSchema>;

export const CreateWebhookSchema = z.object({
  url: z.string().url(),
  events: z.array(WebhookEventSchema).min(1),
  description: z.string().max(500).optional(),
  /** Aláíró kulcs — a backend kódolt formában tárolja, soha nem küldi vissza */
  secret: z.string().min(16).max(128).optional(),
  active: z.boolean().default(true),
});
export type CreateWebhookInput = z.infer<typeof CreateWebhookSchema>;

export const UpdateWebhookSchema = CreateWebhookSchema.partial().omit({ secret: true });
export type UpdateWebhookInput = z.infer<typeof UpdateWebhookSchema>;

export const WebhookDeliverySchema = z.object({
  id: UuidSchema,
  webhookId: UuidSchema,
  event: WebhookEventSchema,
  status: z.enum(['pending', 'success', 'failed']),
  httpStatus: z.number().int().nullable(),
  attempts: z.number().int().nonnegative(),
  errorMessage: z.string().nullable(),
  scheduledAt: IsoDateTimeSchema,
  deliveredAt: IsoDateTimeSchema.nullable(),
  /** Truncated body excerpt; teljes payload külön végponton */
  payloadExcerpt: z.string().nullable(),
});
export type WebhookDelivery = z.infer<typeof WebhookDeliverySchema>;
